import React, { useCallback } from 'react';
import classNames from 'classnames';
import { ErrorSchema, Field } from '@rjsf/utils';
import { MarkdownContent } from '../markdown-content';

const prettyJson = (jsonString: string): string => {
  if (jsonString === '{}') {
    return `{\n\t\n}`;
  }
  return jsonString;
};

export const JsonField: Field = (props) => {
  const {
    onChange,
    onBlur,
    onFocus,
    idSchema,
    schema: { title, description },
    formData,
    readonly,
    disabled,
  } = props;
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const [value, setValue] = React.useState<string>(
    prettyJson(JSON.stringify(formData || {}, null, 2))
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
    },
    [onChange]
  );

  const handleKeyDown = useCallback<
    React.KeyboardEventHandler<HTMLTextAreaElement>
  >(function (event) {
    if (textareaRef.current && event.key == 'Tab') {
      event.preventDefault();
    }
  }, []);

  const handleBlur = useCallback<
    React.FocusEventHandler<HTMLTextAreaElement>
  >(() => {
    try {
      const jsonValue = JSON.parse(value);
      onChange(jsonValue);
      onBlur(idSchema.$id, jsonValue);
    } catch {
      // @ts-ignore
      onChange(null, {
        __errors: ['Invalid JSON'],
      } as ErrorSchema<string>);
      onBlur(idSchema.$id, null);
    }
  }, [value, onBlur, onChange, idSchema]);

  const handleFocus = useCallback<React.FocusEventHandler<HTMLTextAreaElement>>(
    (event) => {
      onFocus(idSchema.$id, event.target.value);
    },
    [onFocus, idSchema]
  );

  const stringValue = value || JSON.stringify(formData, null, 2) || '{}';
  return (
    <div className="form-group">
      {title && (
        <label className="form-label" htmlFor={idSchema.$id}>
          {title}
        </label>
      )}
      <div className={'wrapper'}>
        <textarea
          id={idSchema.$id}
          ref={textareaRef}
          rows={stringValue.split('\n').length}
          value={stringValue}
          className={classNames(['form-control', 'textarea'])}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          readOnly={readonly}
          disabled={disabled}
        />
        <div className={'shadow'}>{value}</div>
      </div>
      <small className="text-muted form-text">
        <MarkdownContent>{description as string}</MarkdownContent>
      </small>
    </div>
  );
};
